"use client";

import { useState, useRef, type SetStateAction } from "react";


type NonFunction = Exclude<any, Function>;


export type StateRefGetter<T> = () => T;
export type StateRefSetter<T> = (update: SetStateAction<T>) => void;
export type StateRef<T> = Readonly<{
	current: T;
	set: StateRefSetter<T>;
}>;


/**
 * combines React.useState with React.useRef to allow usage inside of functions.
 */
export function useStateRef
	<const T extends NonFunction>
		(initializer: T | (() => T)):
			StateRef<T>
{
	const [initialValue, setValue] = useState<T>(initializer);
	const valueRef = useRef<T>(initialValue);

	return {
		get current() {
			return valueRef.current;
		},

		set: update => {
			/* here we re-implement the react setState */
			valueRef.current = (typeof update === "function")
				? (update as (prevState: T) => T)(valueRef.current)
				: update as T;

			/* notify react to re-render */
			setValue(valueRef.current);
		},
	};
}


/**
 * same as useStateRef, with possibly undefined value
 */
export function UseOptionRef
	<const T extends Exclude<{} | null, Function>>
		(initializer?: T | (() => T)):
			StateRef<T | undefined>
{
	return useStateRef<T | undefined>(initializer ?? undefined);
}
